/**
 * UserContext - State management for user authentication and preferences
 *
 * Features:
 * - Subscribes to EventBus for real-time preference updates
 * - Handles sync event for initial state
 * - Persists user data to localStorage
 */

import {createContext, ReactNode, useCallback, useEffect, useState} from "react";
import {Optional} from "../utils/types.ts";
import {SupportedLanguage} from "../utils/i18n.ts";
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
  SYSTEM_LOCAL_STORAGE
} from "../utils/localStorage.ts";
import {getUser, getUserPreferences, resetUserPreferencesToOrganizationDefaults, updateUserPreferences as apiUpdateUserPreference} from "../api/user.ts";
import {clearCookie, setCookie, SYSTEM_COOKIES} from "../utils/cookies.ts";
import {isAuthenticated, TOKEN_EXPIRATION} from "../utils/auth.ts";
import i18n from "i18next";
import {AUTH_STATE_CHANGED_EVENT} from "./OrganizationContext.tsx";
import {
  getEventBus,
  EventTypes,
  UserPayload,
  UserPreferencesPayload,
  NoteTypePayload,
} from "../services/EventBus.ts";

export type WebsocketUserPreferencesPayload = UserPreferences;

export type UserPreferences = {
  id: number;
  interface_language: SupportedLanguage;
  notes_language: SupportedLanguage | null;
  default_note_type_id: number | null;
  resolved_default_note_type_id: number | null;
  resolved_default_followup_note_type_id: number | null;
  resolved_notes_language: SupportedLanguage | null;
  has_custom_defaults: boolean;
  created_at: string;
  updated_at: string;
}

export type User = {
  id: number;
  email: string;
  first_name: Optional<string>;
  last_name: Optional<string>;
  token: Optional<string>;
  preferences: UserPreferences;
}

export type UserContextType = {
  user: Optional<User>;
  setUserToken: (token: Optional<string>) => void;
  setUiLanguage: (language: SupportedLanguage) => void;
  setNoteLanguage: (language: SupportedLanguage) => void;
  logout: () => void;
  updateUserPreferences: (preferences: UserPreferences) => void;
  refreshUser: () => Promise<void>;
  resetNoteDefaults: () => Promise<void>;
  applyLocalDefaultNoteType: (noteTypeId: number) => void;
};

const contextDefaultValues: UserContextType = {
  user: null,
  setUserToken: () => {},
  setUiLanguage: () => {},
  setNoteLanguage: () => {},
  logout: () => {},
  updateUserPreferences: () => {},
  refreshUser: async () => {},
  resetNoteDefaults: async () => {},
  applyLocalDefaultNoteType: () => {},
}

const UserContext = createContext<UserContextType>(contextDefaultValues);

/**
 * Normalize user payload from WebSocket/sync to local User type
 */
function normalizeUserFromPayload(
  payload: UserPayload,
  existingToken: Optional<string>
): User {
  return {
    id: payload.id,
    email: payload.email,
    first_name: payload.first_name ?? null,
    last_name: payload.last_name ?? null,
    token: existingToken,
    preferences: normalizePreferencesFromPayload(payload.preferences),
  };
}

/**
 * Normalize preferences payload from WebSocket to local UserPreferences type
 */
function normalizePreferencesFromPayload(payload: UserPreferencesPayload): UserPreferences {
  return {
    id: payload.id,
    interface_language: (payload.default_language as SupportedLanguage) ?? 'en',
    notes_language: (payload.default_language as SupportedLanguage) ?? 'en',
    default_note_type_id: payload.default_note_type_id ?? 0,
    resolved_default_note_type_id: payload.default_note_type_id ?? null,
    resolved_default_followup_note_type_id: payload.default_followup_note_type_id ?? null,
    resolved_notes_language: (payload.default_language as SupportedLanguage) ?? null,
    has_custom_defaults: false,
    created_at: '',
    updated_at: '',
  };
}

function UserProvider({children}: {children: ReactNode}) {
  const [user, setUser] = useState<Optional<User>>(JSON.parse(getLocalStorageItem(SYSTEM_LOCAL_STORAGE.USER) || 'null'));
  const eventBus = getEventBus();

  /**
   * Update user from WebSocket event (without triggering API call)
   */
  const updateUserFromEvent = useCallback((userPayload: UserPayload) => {
    setUser(prev => {
      if (!prev) return prev;
      const normalized = normalizeUserFromPayload(userPayload, prev.token);
      // Preserve local preferences that might not be in payload
      const mergedUser: User = {
        ...normalized,
        preferences: {
          ...prev.preferences,
          ...normalized.preferences,
          // Preserve HTTP-fetched resolved values — WS payload doesn't carry them
          resolved_default_note_type_id: prev.preferences.resolved_default_note_type_id,
          resolved_default_followup_note_type_id: prev.preferences.resolved_default_followup_note_type_id,
          resolved_notes_language: prev.preferences.resolved_notes_language,
          has_custom_defaults: prev.preferences.has_custom_defaults,
        },
      };
      if (i18n.language !== mergedUser.preferences.interface_language) {
        i18n.changeLanguage(mergedUser.preferences.interface_language);
      }
      setLocalStorageItem(SYSTEM_LOCAL_STORAGE.USER, JSON.stringify(mergedUser));
      return mergedUser;
    });
  }, []);

  /**
   * Update preferences from WebSocket event
   */
  const updatePreferencesFromEvent = useCallback((prefsPayload: UserPreferencesPayload) => {
    setUser(prev => {
      if (!prev) return prev;
      const normalized = normalizePreferencesFromPayload(prefsPayload);
      const mergedUser: User = {
        ...prev,
        preferences: {
          ...prev.preferences,
          ...normalized,
          // Preserve HTTP-fetched resolved values — WS payload doesn't carry them
          resolved_default_note_type_id: prev.preferences.resolved_default_note_type_id,
          resolved_default_followup_note_type_id: prev.preferences.resolved_default_followup_note_type_id,
          resolved_notes_language: prev.preferences.resolved_notes_language,
          has_custom_defaults: prev.preferences.has_custom_defaults,
        },
      };
      if (i18n.language !== mergedUser.preferences.interface_language) {
        i18n.changeLanguage(mergedUser.preferences.interface_language);
      }
      setLocalStorageItem(SYSTEM_LOCAL_STORAGE.USER, JSON.stringify(mergedUser));
      return mergedUser;
    });
  }, []);

  /**
   * Update default note type from WebSocket event
   */
  const updateDefaultNoteTypeFromEvent = useCallback((noteType: NoteTypePayload) => {
    setUser(prev => {
      if (!prev) return prev;
      const mergedUser: User = {
        ...prev,
        preferences: {
          ...prev.preferences,
          default_note_type_id: noteType.id,
          resolved_default_note_type_id: noteType.id,
          has_custom_defaults: true,
        },
      };
      setLocalStorageItem(SYSTEM_LOCAL_STORAGE.USER, JSON.stringify(mergedUser));
      return mergedUser;
    });
  }, []);

  // Subscribe to EventBus for real-time updates
  useEffect(() => {
    const subscriptions = [
      // Full state sync - update user data
      eventBus.subscribe(EventTypes.SYNC, (payload) => {
        if (payload.user) {
          updateUserFromEvent(payload.user);
        }
      }),

      // User preferences changed
      eventBus.subscribe(EventTypes.USER_PREFERENCES_CHANGED, (payload) => {
        updatePreferencesFromEvent(payload);
      }),

      // Default note type changed
      eventBus.subscribe(EventTypes.DEFAULT_NOTE_TYPE_CHANGED, (payload) => {
        updateDefaultNoteTypeFromEvent(payload);
      }),
    ];

    return () => {
      subscriptions.forEach(sub => sub.unsubscribe());
    };
  }, [eventBus, updateUserFromEvent, updatePreferencesFromEvent, updateDefaultNoteTypeFromEvent]);

  // Initial load effect (fallback when WebSocket not connected)
  useEffect(() => {
    async function getAndSetUser() {
      const response: User = await getUser();
      const preferences: UserPreferences = await getUserPreferences();
      const fullUser: User = {
        ...response,
        preferences
      };
      persistUserData(fullUser);
    }

    async function getAndSetUserPreferences() {
      const response: UserPreferences = await getUserPreferences();
      const fullUser: User = {
        ...user!,
        preferences: response
      }
      persistUserData(fullUser);
    }

    if(isAuthenticated()) {
      if(!user) {
        getAndSetUser();
      } else {
        getAndSetUserPreferences();
      }
    }
  }, []);
  
  async function handleSetUserToken(token: Optional<string>) {
    if(token) {
      setCookie(SYSTEM_COOKIES.TOKEN, token, TOKEN_EXPIRATION);
      const userResponse: User = await getUser();
      const preferences: UserPreferences = await getUserPreferences();
      const fullUser: User = {
        ...userResponse,
        token: token,
        preferences
      }
      persistUserData(fullUser);
      // Notify other contexts (e.g., OrganizationContext) that auth state changed
      window.dispatchEvent(new CustomEvent(AUTH_STATE_CHANGED_EVENT));
    } else {
      removeLocalStorageItem(SYSTEM_LOCAL_STORAGE.USER);
      setUser(null);
      window.dispatchEvent(new CustomEvent(AUTH_STATE_CHANGED_EVENT));
    }
  }
  
  function setUiLanguage(language: SupportedLanguage) {
    if(!user) throw new Error('Cannot set UI language: no user logged in');
    const newUser: User = {
      ...user,
      preferences: {
        ...user.preferences,
        interface_language: language
      }
    }
    persistUserData(newUser);
  }
  
  function setNoteLanguage(language: SupportedLanguage) {
    if(!user) throw new Error('Cannot set note language: no user logged in');
    const newUser: User = {
      ...user,
      preferences: {
        ...user.preferences,
        notes_language: language
      }
    }
    persistUserData(newUser);
  }
  
  function logout() {
    clearCookie(SYSTEM_COOKIES.TOKEN);
    removeLocalStorageItem(SYSTEM_LOCAL_STORAGE.USER);
    removeLocalStorageItem(SYSTEM_LOCAL_STORAGE.CURRENT_ORGANIZATION_ID);
    setUser(null);
    window.dispatchEvent(new CustomEvent(AUTH_STATE_CHANGED_EVENT));
  }
  
  function persistUserData(user: User) {
    if(i18n.language !== user.preferences.interface_language) {
      i18n.changeLanguage(user.preferences.interface_language);
    }
    setUser(user);
    apiUpdateUserPreference(user.preferences);
    setLocalStorageItem(SYSTEM_LOCAL_STORAGE.USER, JSON.stringify(user));
  }
  
  function updateUserPreferences(preferences: UserPreferences) {
    if(!user) throw new Error('Cannot update preferences: no user logged in');
    const newUser: User = {
      ...user,
      preferences: {
        ...user.preferences,
        ...preferences
      }
    }
    persistUserData(newUser);
  }

  async function refreshUser() {
    if(!isAuthenticated()) return;
    const response: User = await getUser();
    const preferences: UserPreferences = await getUserPreferences();
    const fullUser: User = {
      ...response,
      token: user?.token ?? null,
      preferences
    };
    setUser(fullUser);
    setLocalStorageItem(SYSTEM_LOCAL_STORAGE.USER, JSON.stringify(fullUser));
  }

  async function resetNoteDefaults() {
    if(!user) throw new Error('Cannot reset note defaults: no user logged in');
    const preferences: UserPreferences = await resetUserPreferencesToOrganizationDefaults();
    const newUser: User = { ...user, preferences };
    setUser(newUser);
    setLocalStorageItem(SYSTEM_LOCAL_STORAGE.USER, JSON.stringify(newUser));
  }

  function applyLocalDefaultNoteType(noteTypeId: number) {
    if (!user) return;
    const newUser: User = {
      ...user,
      preferences: {
        ...user.preferences,
        default_note_type_id: noteTypeId,
        resolved_default_note_type_id: noteTypeId,
        has_custom_defaults: true,
      },
    };
    setUser(newUser);
    setLocalStorageItem(SYSTEM_LOCAL_STORAGE.USER, JSON.stringify(newUser));
  }

  return <UserContext.Provider value={{
    updateUserPreferences,
    logout,
    user,
    setUserToken: handleSetUserToken,
    setUiLanguage,
    setNoteLanguage,
    refreshUser,
    resetNoteDefaults,
    applyLocalDefaultNoteType,
  }}>{children}</UserContext.Provider>;
}

export {UserContext, UserProvider};
